import React, { useState, useRef, useCallback } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, XCircle, Download, ArrowLeft, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SAMPLE_CSV = `Name,Role,Email,Linked Athletes,Password,Gender
John Doe,Player,john_doe@pfaphub.com,John Doe,,Male
Jane Smith,Staff,jane_smith@pfaphub.com,,SecureP@ss1,
Maria Parent,Parent,maria_parent@pfaphub.com,John Doe;Jane Athlete,,`;

const BulkImport = () => {
  const [step, setStep] = useState('upload'); // upload | preview | results
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [preview, setPreview] = useState(null);
  const [overwriteFlags, setOverwriteFlags] = useState({});
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);

  const downloadTemplate = () => {
    const blob = new Blob([SAMPLE_CSV], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pfaphub_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const processFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/admin/bulk-import/preview`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      setOverwriteFlags({});
      setStep('preview');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse CSV');
    } finally {
      setUploading(false);
    }
  }, []);

  const onFileSelect = (e) => {
    processFile(e.target.files?.[0]);
    e.target.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    processFile(e.dataTransfer.files?.[0]);
  };

  const toggleOverwrite = (idx) => {
    setOverwriteFlags(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const handleConfirm = async () => {
    if (!preview) return;
    const rows = preview.rows
      .filter(r => r.status !== 'error')
      .map((r, idx) => ({
        name: r.name,
        role: r.role,
        email: r.email,
        linked_athletes: r.linked_athletes,
        password: r.password,
        gender: r.gender || '',
        overwrite: !!overwriteFlags[idx],
      }));

    if (rows.length === 0) {
      toast.error('No valid rows to import');
      return;
    }

    setConfirming(true);
    try {
      const res = await axios.post(`${API}/admin/bulk-import/confirm`, { rows });
      setResults(res.data);
      setStep('results');
      toast.success('Import completed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setConfirming(false);
    }
  };

  const reset = () => {
    setStep('upload');
    setPreview(null);
    setOverwriteFlags({});
    setResults(null);
  };

  const statusIcon = (status) => {
    if (status === 'valid') return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
    if (status === 'warning') return <AlertTriangle className="w-4 h-4 text-amber-500" />;
    return <XCircle className="w-4 h-4 text-red-500" />;
  };

  const statusRowClass = (status) => {
    if (status === 'error') return 'bg-red-50 dark:bg-red-950/20';
    if (status === 'warning') return 'bg-amber-50 dark:bg-amber-950/20';
    return '';
  };

  const importableCount = preview ? preview.rows.filter(r => r.status !== 'error').length : 0;
  const errorCount = preview ? preview.rows.filter(r => r.status === 'error').length : 0;

  return (
    <Layout>
      <div className="p-6 lg:p-8" data-testid="bulk-import-page">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Bulk Import
          </h2>
          <p className="text-gray-600 dark:text-gray-500 mt-1">Import athletes, staff, and parents from a CSV file</p>
        </div>

        {/* STEP 1: Upload */}
        {step === 'upload' && (
          <Card data-testid="upload-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                Upload CSV
              </CardTitle>
              <CardDescription>Upload a CSV file with account data. Download the template below to get started.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
                <Download className="w-4 h-4 mr-2" /> Download Template
              </Button>

              <div
                className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors cursor-pointer ${
                  dragging
                    ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-950/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-emerald-300 dark:hover:border-emerald-700'
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                data-testid="dropzone"
              >
                <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={onFileSelect} data-testid="file-input" />
                {uploading ? (
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-10 h-10 text-emerald-500 animate-spin" />
                    <p className="text-gray-600 dark:text-gray-400">Processing CSV...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <Upload className="w-10 h-10 text-gray-400" />
                    <p className="text-gray-600 dark:text-gray-400">Drag & drop your CSV here, or click to browse</p>
                    <p className="text-xs text-gray-400">Accepted format: .csv</p>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 text-sm">
                <p className="font-medium text-gray-700 dark:text-gray-300 mb-2">CSV Format</p>
                <div className="overflow-x-auto">
                  <table className="text-xs text-gray-600 dark:text-gray-400 font-mono">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="pr-4 pb-1 text-left">Name</th>
                        <th className="pr-4 pb-1 text-left">Role</th>
                        <th className="pr-4 pb-1 text-left">Email</th>
                        <th className="pr-4 pb-1 text-left">Linked Athletes</th>
                        <th className="pr-4 pb-1 text-left">Password</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td className="pr-4 py-0.5">John Doe</td><td className="pr-4">Player</td><td className="pr-4">john@pfaphub.com</td><td className="pr-4">John Doe</td><td className="pr-4 text-gray-400">(auto)</td></tr>
                      <tr><td className="pr-4 py-0.5">Jane Parent</td><td className="pr-4">Parent</td><td className="pr-4">jane@pfaphub.com</td><td className="pr-4">John Doe; Jane A.</td><td className="pr-4 text-gray-400">(auto)</td></tr>
                      <tr><td className="pr-4 py-0.5">New Coach</td><td className="pr-4">Staff</td><td className="pr-4">coach@pfaphub.com</td><td className="pr-4"></td><td className="pr-4">MyP@ss123</td></tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-400 mt-2">Passwords are auto-generated if left empty. Use semicolons (;) to separate multiple athlete names. Optional <code>Gender</code> column accepts Male / Female / M / F / Other / Prefer not to say (only set on linked athletes that don't already have one).</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* STEP 2: Preview */}
        {step === 'preview' && preview && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Button variant="ghost" onClick={reset} data-testid="back-to-upload-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <div className="flex items-center gap-4">
                <div className="flex gap-3 text-sm">
                  <span className="flex items-center gap-1 text-emerald-600">
                    <CheckCircle2 className="w-4 h-4" /> {preview.summary.valid} valid
                  </span>
                  <span className="flex items-center gap-1 text-amber-600">
                    <AlertTriangle className="w-4 h-4" /> {preview.summary.warnings} warnings
                  </span>
                  <span className="flex items-center gap-1 text-red-600">
                    <XCircle className="w-4 h-4" /> {preview.summary.errors} errors
                  </span>
                </div>
                <Button
                  onClick={handleConfirm}
                  disabled={confirming || importableCount === 0}
                  data-testid="confirm-import-btn"
                >
                  {confirming ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing...</>
                  ) : (
                    `Import ${importableCount} Account${importableCount !== 1 ? 's' : ''}`
                  )}
                </Button>
              </div>
            </div>

            {errorCount > 0 && (
              <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300" data-testid="error-banner">
                {errorCount} row(s) have errors and will be skipped during import. Fix your CSV and re-upload to include them.
              </div>
            )}

            <Card>
              <CardContent className="p-0">
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50 dark:bg-gray-800/50">
                        <TableHead className="w-10 font-semibold">#</TableHead>
                        <TableHead className="w-10 font-semibold">Status</TableHead>
                        <TableHead className="font-semibold">Name</TableHead>
                        <TableHead className="font-semibold">Role</TableHead>
                        <TableHead className="font-semibold">Email</TableHead>
                        <TableHead className="font-semibold">Linked Athletes</TableHead>
                        <TableHead className="font-semibold">Password</TableHead>
                        <TableHead className="font-semibold">Issues</TableHead>
                        <TableHead className="w-20 font-semibold">Overwrite</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.rows.map((row, idx) => (
                        <TableRow key={idx} className={statusRowClass(row.status)} data-testid={`preview-row-${idx}`}>
                          <TableCell className="text-xs text-gray-400">{row.row_number}</TableCell>
                          <TableCell>{statusIcon(row.status)}</TableCell>
                          <TableCell className="font-medium text-sm">{row.name || <span className="text-red-400 italic">missing</span>}</TableCell>
                          <TableCell>
                            {row.role ? (
                              <Badge variant="outline" className="text-xs">{row.role}</Badge>
                            ) : <span className="text-red-400 italic text-xs">missing</span>}
                          </TableCell>
                          <TableCell className="text-sm font-mono text-gray-600 dark:text-gray-400">
                            {row.email || <span className="text-red-400 italic">missing</span>}
                          </TableCell>
                          <TableCell>
                            {row.resolved_athletes?.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {row.resolved_athletes.map((a, i) => (
                                  <Badge key={i} variant="outline" className="text-[10px]">{a.name}</Badge>
                                ))}
                              </div>
                            ) : row.linked_athletes ? (
                              <span className="text-xs text-amber-500">{row.linked_athletes}</span>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="text-xs font-mono">
                              {row.password_generated ? (
                                <span className="text-emerald-600 dark:text-emerald-400" title={row.password}>auto</span>
                              ) : (
                                <span className="text-gray-500">custom</span>
                              )}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-0.5">
                              {row.errors?.map((e, i) => (
                                <p key={i} className="text-xs text-red-600 dark:text-red-400">{e}</p>
                              ))}
                              {row.warnings?.map((w, i) => (
                                <p key={i} className="text-xs text-amber-600 dark:text-amber-400">{w}</p>
                              ))}
                              {!row.errors?.length && !row.warnings?.length && (
                                <span className="text-xs text-gray-400">—</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            {row.email_exists && row.status !== 'error' ? (
                              <Checkbox
                                checked={!!overwriteFlags[idx]}
                                onCheckedChange={() => toggleOverwrite(idx)}
                                data-testid={`overwrite-checkbox-${idx}`}
                              />
                            ) : (
                              <span className="text-xs text-gray-300">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* STEP 3: Results */}
        {step === 'results' && results && (
          <div className="space-y-4" data-testid="results-section">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  Import Complete
                </CardTitle>
                <CardDescription>Summary of the bulk import operation</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  <div className="bg-emerald-50 dark:bg-emerald-950/20 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400" data-testid="created-count">{results.created?.length || 0}</p>
                    <p className="text-xs text-emerald-600 dark:text-emerald-500 mt-1">Created</p>
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-950/20 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-blue-700 dark:text-blue-400" data-testid="updated-count">{results.updated?.length || 0}</p>
                    <p className="text-xs text-blue-600 dark:text-blue-500 mt-1">Updated</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-gray-700 dark:text-gray-400" data-testid="skipped-count">{results.skipped?.length || 0}</p>
                    <p className="text-xs text-gray-500 mt-1">Skipped</p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-950/20 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-red-700 dark:text-red-400" data-testid="error-count">{results.errors?.length || 0}</p>
                    <p className="text-xs text-red-600 dark:text-red-500 mt-1">Errors</p>
                  </div>
                </div>

                {results.created?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Created:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {results.created.map((c, i) => (
                        <Badge key={i} className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 text-xs">{c}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {results.updated?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Updated:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {results.updated.map((u, i) => (
                        <Badge key={i} className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 text-xs">{u}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {results.skipped?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Skipped (already exist):</p>
                    <div className="flex flex-wrap gap-1.5">
                      {results.skipped.map((s, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{s}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {results.errors?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-red-600 dark:text-red-400 mb-1">Errors:</p>
                    {results.errors.map((e, i) => (
                      <p key={i} className="text-xs text-red-600 dark:text-red-400">{e}</p>
                    ))}
                  </div>
                )}

                <div className="pt-4 border-t dark:border-gray-700">
                  <Button onClick={reset} data-testid="import-another-btn">
                    <Upload className="w-4 h-4 mr-2" /> Import Another CSV
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BulkImport;
